import _extends from "@babel/runtime/helpers/extends";
import React from "react";
import { Badge, Box, Button, Card, Grid, Icon, Text } from "~/components-shared";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import moment from "moment";
import lists from "~/lib/lists";
import { AuthoritiesEnum, RoutesEnum } from "~/types/enums";
import useAbacExt from "~/hooks/abac";
const Scrollable = styled.div`
  overflow: auto;
`;
const StyledGrid = styled(Grid)`
  overflow: hidden;
  position: relative;
`;
const StyledButton = styled(Button)`
  & span {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;
const ProgressFlowDetailsCard = _ref => {
  let {
    progressFlowItem,
    ...rest
  } = _ref;
  const {
    t
  } = useTranslation();
  const theme = useTheme();
  const {
    userHas
  } = useAbacExt();
  const handleTopicClick = topicId => {
    const url = `${window.location.origin}${RoutesEnum.TOPIC.replace(":topicId", encodeURIComponent(topicId))}?view=footprints`;
    window.open(url, "_blank");
  };
  return /*#__PURE__*/React.createElement(Card, _extends({
    s: {
      py: 0,
      px: 2
    },
    fullWidth: true
  }, rest), /*#__PURE__*/React.createElement(Scrollable, null, /*#__PURE__*/React.createElement(Box, {
    s: {
      pr: 5
    }
  }, /*#__PURE__*/React.createElement(Text, {
    element: "h3"
  }, t("ProgressFlowDetails.itemDetails")), /*#__PURE__*/React.createElement(Box, {
    gap: 1,
    s: {
      mt: 2,
      mb: 8
    },
    fullWidth: true
  }, progressFlowItem.itemType && /*#__PURE__*/React.createElement(Badge, {
    color: "translucent",
    s: {
      px: 3,
      py: 0.5
    }
  }, lists.insights.type.names[progressFlowItem.itemType]), progressFlowItem.isDismissed && /*#__PURE__*/React.createElement(Badge, {
    color: "translucentDanger",
    s: {
      px: 3,
      py: 0.5
    }
  }, t("ProgressFlowTableRow.dismissed")), progressFlowItem.isClosed && /*#__PURE__*/React.createElement(Badge, {
    color: "translucentSuccess",
    s: {
      px: 3,
      py: 0.5
    }
  }, t("ProgressFlowTableRow.completed"))), /*#__PURE__*/React.createElement(StyledGrid, {
    cols: 1,
    gap: 2,
    template: "1fr",
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "h6"
  }, t("ProgressFlowDetails.context")), /*#__PURE__*/React.createElement(Text, {
    element: "p",
    color: "textSecondary",
    fontSize: 4,
    lineHeight: 6,
    s: {
      mb: 8,
      mt: 4
    }
  }, progressFlowItem.summary || t("ProgressFlowDetails.noSummary"))), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "h6"
  }, t("ProgressFlowDetails.action")), /*#__PURE__*/React.createElement(Text, {
    element: "p",
    color: "textSecondary",
    fontSize: 4,
    lineHeight: 6,
    s: {
      mb: 5,
      mt: 2
    }
  }, progressFlowItem.context || t("ProgressFlowDetails.noContext"))), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "h6"
  }, t("ProgressFlowDetails.topic")), /*#__PURE__*/React.createElement(Box, {
    gap: 1,
    s: {
      my: 2
    }
  }, progressFlowItem.topics.map(topic => /*#__PURE__*/React.createElement(StyledButton, {
    key: topic.id,
    onClick: () => handleTopicClick(topic.id),
    fontSize: 3,
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": topic.name,
    s: {
      px: 2,
      py: 1
    },
    maxWidth: "200px",
    color: "translucentPrimary"
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "timelapse",
    s: {
      mr: 1,
      mt: -0.5
    }
  }), topic.name))), userHas(AuthoritiesEnum.EXPERIMENTAL) && /*#__PURE__*/React.createElement(Text, {
    element: "p",
    color: "textSecondary",
    fontSize: 4,
    lineHeight: 6,
    s: {
      mb: 5,
      mt: 2
    }
  }, progressFlowItem.topicExplanation?.explanation || t("ProgressFlowDetails.unknown"))), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Box, {
    gap: 2,
    align: "center"
  }, /*#__PURE__*/React.createElement(Text, {
    element: "h6"
  }, t("ProgressFlowDetails.urgency")), /*#__PURE__*/React.createElement(Badge, {
    color: "grey",
    s: {
      px: 3,
      py: 0.5
    }
  }, /*#__PURE__*/React.createElement(Text, {
    color: lists.insights.urgency.colors(theme)[progressFlowItem.impact]
  }, progressFlowItem.impact || t("ProgressFlowDetails.unknown")))), /*#__PURE__*/React.createElement(Text, {
    element: "p",
    color: "textSecondary",
    fontSize: 4,
    lineHeight: 6,
    s: {
      mb: 5,
      mt: 2
    }
  }, progressFlowItem.impactExplanation || t("ProgressFlowDetails.unknown"))), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Grid, {
    cols: 2
  }, /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "h6"
  }, t("ProgressFlowDetails.observedAt")), /*#__PURE__*/React.createElement(Text, {
    element: "p",
    color: "textSecondary",
    fontSize: 4,
    lineHeight: 6,
    s: {
      mb: 5,
      mt: 2
    }
  }, moment(progressFlowItem.observedAt).format("ddd, MMM Do") || t("ProgressFlowDetails.unknown"))), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "h6"
  }, t("ProgressFlowDetails.closedAt")), /*#__PURE__*/React.createElement(Text, {
    element: "p",
    color: "textSecondary",
    fontSize: 4,
    lineHeight: 6,
    s: {
      mb: 5,
      mt: 2
    }
  }, moment(progressFlowItem.closedAt).format("ddd, MMM Do") || t("ProgressFlowDetails.open"))))), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "h6"
  }, t("ProgressFlowDetails.owner")), /*#__PURE__*/React.createElement(Text, {
    element: "p",
    color: "textSecondary",
    fontSize: 4,
    lineHeight: 6,
    s: {
      mb: 5,
      mt: 2
    }
  }, /*#__PURE__*/React.createElement(Box, {
    gap: 2
  }, progressFlowItem.owner.map(owner => /*#__PURE__*/React.createElement(Text, {
    key: owner,
    color: "textSecondary"
  }, owner))))), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    element: "h6"
  }, t("ProgressFlowDetails.links")), /*#__PURE__*/React.createElement(Text, {
    element: "p",
    color: "textSecondary",
    fontSize: 4,
    lineHeight: 6,
    s: {
      mb: 5,
      mt: 2
    }
  }, /*#__PURE__*/React.createElement(Box, {
    gap: 2
  }, progressFlowItem.links?.length ? progressFlowItem.links.map(link => /*#__PURE__*/React.createElement(Text, {
    key: link.id,
    color: "textSecondary",
    display: "block"
  }, link.itemContent, " ", /*#__PURE__*/React.createElement(Badge, {
    color: "translucent"
  }, link.mentionType))) : "-")))))));
};
export default ProgressFlowDetailsCard;